/**
 * Plan-approved → build-running handoff policy for the new UI.
 *
 * See `docs/plan-build-handoff-implementation-spec.md` for the user-facing
 * contract and regression map.
 */

import type {
  AutoBuildStatus,
  BuildGameInterviewStatus,
} from '@volley/crucible-ipc-types/build-game-types';
import {
  isPreviewPhaseGated,
  previewPhaseGateTooltip,
  type PreviewPhaseGate,
} from '../components/PreviewLaunchButton';
import { PREVIEW_PHASE_TOTAL } from '@volley/crucible-ipc-types/preview-gate-constants';
import type { WorkspaceRunKind } from './run-state';
import type { PreviewDevLoopKind, PreviewDevLoopState } from './preview-state';

export type WorkspaceHandoffMode = 'chat' | 'plan' | 'preview' | 'design';

/** Three-zone build layout: preview only while Phase B is actively running. */
export function showNewUiBuildViewFor(runStateKind: WorkspaceRunKind): boolean {
  return runStateKind === 'building';
}

/**
 * crucible#3617 — a generated plan is a visible approval boundary for the
 * Prosumer Guided Interview. Consumer retains its purpose-built north-star
 * auto-approval path; legacy/in-flight auto-builds still veto navigation at
 * the eligibility gate.
 */
export function planHandoffVisibleFor(args: { showPlanModeView: boolean }): boolean {
  return args.showPlanModeView;
}

/**
 * Auto-build statuses that mean the build WILL proceed without the user AND
 * whose CLEARING is durable. `commit-starting` is deliberately excluded — see
 * isActiveAutoBuild.
 */
const ACTIVE_AUTO_BUILD_STATUSES: ReadonlySet<AutoBuildStatus> = new Set([
  'armed',
  'generating-docs',
  'exporting-package',
]);

/**
 * #3038 — is an auto-build still IN FLIGHT (it will proceed to build without the
 * user)? Only an active auto-build vetoes plan-review navigation.
 *
 * Codex R3 / Bugbot: `autoBuildState != null` was too broad — the back-to-plan
 * IPC preserves the top-level autoBuild fields, so a COMPLETED Fast/Just-Build
 * that later returns to a genuine `waiting-for-plan-review` still carried
 * `status: 'done'`. Treating that as armed permanently vetoed the gate.
 *
 * Codex R4 (HIGH): `commit-starting` is a STEALTH-terminal. The runtime sets it
 * before dispatch and only BEST-EFFORT clears it to `done`
 * (`reportBuildDispatched` ignores a losing `setStatus` on a state-lock
 * conflict), so a SUCCESSFUL dispatch can leave it persisted forever;
 * back-to-plan then preserves it and it vetoes every later genuine gate — the
 * same corpse-vote as `done`. It is EXCLUDED from the veto: fail-open with
 * self-correction beats fail-closed forever. A genuinely-live `commit-starting`
 * can only coincide with `waiting-for-plan-review` via a brief cross-source
 * render race (run-state's plan-review is snapshot-derived and takes precedence
 * over auto-build; the auto-build status comes from interview-status), and if
 * the gate fires in that window the return leg self-corrects when the build goes
 * active. Root cause tracked in the runtime follow-up (see PR / #3038).
 *
 * So only the in-flight statuses whose clearing is durable veto; `commit-starting`
 * and the terminal statuses (done / cancelled / failed) and null do NOT.
 */
export function isActiveAutoBuild(
  autoBuild: { status: AutoBuildStatus } | null | undefined,
): boolean {
  return autoBuild != null && ACTIVE_AUTO_BUILD_STATUSES.has(autoBuild.status);
}

/**
 * #3038 forward-gate eligibility — is this a genuine plan-review gate that
 * needs the user?
 *
 * The plan-handoff effect used to navigate whenever the plan doc became visible,
 * regardless of whether a review gate actually needed the user. So an
 * auto-proceeding build (plan generated, no approval click) still parked the
 * view on the plan doc — hiding the live build-watch status row (#3025 / PR-B)
 * at the exact dead-air-prone moment. Navigation is the intrusive act, so it
 * requires POSITIVE evidence of a real gate: the plan hand-off is visible, the
 * run-state is `waiting-for-plan-review`, AND the build is not auto-proceeding.
 * The `autoBuildArmed` veto handles the timing the brief flagged — the auto-build
 * flag is armed at create time and is already present, so an auto-build never
 * clears this eligibility even if the other two momentarily line up.
 */
export function planReviewNavigationEligible(args: {
  planHandoffVisible: boolean;
  runStateKind: WorkspaceRunKind;
  autoBuildArmed: boolean;
}): boolean {
  return (
    args.planHandoffVisible &&
    args.runStateKind === 'waiting-for-plan-review' &&
    !args.autoBuildArmed
  );
}

/**
 * #3038 forward gate — fire on the rising edge of ELIGIBILITY, not of raw plan
 * visibility.
 *
 * Bugbot #3044: the two signals feeding eligibility land on DIFFERENT renders —
 * `planHandoffVisible` derives from interview-status (plan content), while
 * `waiting-for-plan-review` derives from the foundry snapshot's plan-review
 * pending action. Keying the gate off the plan-visibility edge (with run-state
 * as a same-render side condition) consumed the edge before run-state qualified,
 * so a genuine gated review whose visibility arrived first never auto-opened the
 * plan. Keying off the eligibility edge fires the moment ALL conditions hold,
 * whichever of the two signals lands last, and still fires exactly once.
 */
export function shouldAutoNavigateToPlanReview(args: {
  eligibleNow: boolean;
  previouslyEligible: boolean;
}): boolean {
  return args.eligibleNow && !args.previouslyEligible;
}

/** Run-state kinds where a build is actively running (Phase B in flight). */
export function isActiveBuildRunKind(runStateKind: WorkspaceRunKind): boolean {
  return runStateKind === 'building' || runStateKind === 'build-kickoff-pending';
}

/**
 * #3038 return leg — should the workspace navigate BACK from the plan surface to
 * the build surface?
 *
 * On a genuine gated review the forward gate auto-navigated to the plan doc.
 * After the user clicks Start Building (or the plan otherwise resolves), the old
 * code left them parked there — status row hidden again through the manual-gate
 * path. When the AUTO-set plan review resolves into a running build, return to
 * the build surface so the live status row is visible.
 *
 * `planViewWasAutoSet` is the guard the brief required: it is true ONLY when the
 * forward gate did the navigation, so a MANUAL navigation to the plan during a
 * running build (flag false) is never yanked. Returns false once the user has
 * already left the plan/design surface (nothing to return).
 */
export function shouldReturnToBuildFromPlan(args: {
  planViewWasAutoSet: boolean;
  currentMode: string;
  runStateKind: WorkspaceRunKind;
}): boolean {
  return (
    args.planViewWasAutoSet &&
    isActiveBuildRunKind(args.runStateKind) &&
    (args.currentMode === 'plan' || args.currentMode === 'design')
  );
}

export function initialWorkspaceModeForHandoff(args: {
  buildView: boolean;
  previewReady: boolean;
  showPlanModeView: boolean;
  planReadyMode?: Extract<WorkspaceHandoffMode, 'plan' | 'design'>;
  createHandoffMode?: Extract<WorkspaceHandoffMode, 'design'> | null;
}): WorkspaceHandoffMode {
  if (args.createHandoffMode === 'design') return 'design';
  if (args.previewReady) return 'preview';
  if (args.showPlanModeView) return args.planReadyMode ?? 'plan';
  return 'chat';
}

/**
 * crucible#2990 — is the CURRENT game's preview session ready?
 *
 * The Workspace persists across in-place game switches (the project-title
 * dropdown changes the route param without unmounting), and the preview
 * session state lives on that persistent instance. The old initial-mode
 * check (`preview.state.status === 'ready'`) therefore counted a ready
 * session belonging to the PREVIOUS game — so switching from a game with a
 * running preview to a sibling initialised the sibling straight into the
 * full-bleed Preview tab. The session payload carries its own `gameId` for
 * both the local and cloud shapes; only a ready session for THIS game may
 * drive preview-first initialisation.
 */
export function previewSessionReadyForGame(
  previewState: { status: string; session?: { gameId: string } },
  gameId: string | null | undefined,
): boolean {
  if (previewState.status !== 'ready') return false;
  if (!gameId) return false;
  return previewState.session?.gameId === gameId;
}

/** The initial surface a freshly-opened game lands on: a workspace mode
 *  plus whether the live-preview split should be docked open in chat. */
export interface InitialWorkspaceSurface {
  mode: WorkspaceHandoffMode;
  openLivePreviewSplit: boolean;
}

/**
 * crucible#2990 — initial-mode routing that can never strand a game on a
 * surface with no chat.
 *
 * With the live-preview flag ON, the header's Chat/Build pill is REPLACED
 * by the Develop pill, so the full-bleed Preview tab (`mode: 'preview'`)
 * has no chat DOM at all — no tabs, no message list, no composer — and no
 * visible affordance back to chat. Initialising a game into that mode
 * (previously done whenever a ready preview session existed) made the game
 * unreachable via chat: build-player-tv-kart rendered ONLY header pills +
 * the preview overlay on every visit. Instead, land on chat with the
 * live-preview split docked open — the ready session stays visible AND the
 * composer is on screen.
 *
 * With live-preview OFF the Preview tab is the only preview surface and
 * the header keeps the Build (chat) pill as an escape hatch, so the old
 * preview-first behaviour is preserved there.
 */
export function initialWorkspaceSurfaceForHandoff(args: {
  buildView: boolean;
  previewReady: boolean;
  showPlanModeView: boolean;
  planReadyMode?: Extract<WorkspaceHandoffMode, 'plan' | 'design'>;
  createHandoffMode?: Extract<WorkspaceHandoffMode, 'design'> | null;
  /** `flags.livePreview` — whether the Develop split exists. */
  livePreviewEnabled: boolean;
}): InitialWorkspaceSurface {
  const mode = initialWorkspaceModeForHandoff(args);
  if (mode === 'preview' && args.livePreviewEnabled) {
    return { mode: 'chat', openLivePreviewSplit: true };
  }
  return { mode, openLivePreviewSplit: false };
}

/**
 * #3038 (Codex R5) — the ENTRY door: the surface a game lands on when the
 * Workspace first initialises it (mount / reload / deep-link / in-place switch).
 *
 * This is the third navigation door, alongside the forward gate and the mount
 * seed in usePlanReviewNavigation. It routed to the plan surface on
 * `planHandoffVisible` ALONE, so entering while an auto-build is ACTIVE (which
 * has often already seeded the plan doc) landed the user on the plan through the
 * whole live build — and the hook correctly refuses to arm the return leg for an
 * active auto-build, so nothing brought them back. Same #3038 failure, parallel
 * entrance.
 *
 * Fix: one predicate, everywhere. Route to the plan on entry only when the SAME
 * `planReviewNavigationEligible` conjunction the forward gate uses holds (plan
 * visible AND waiting-for-plan-review AND not an active auto-build). Otherwise
 * land on the chat/build surface where the live status row lives. A genuine gate
 * still routes to plan; if run-state lags visibility at entry, the forward gate
 * picks it up a render later and arms the return leg.
 */
export function initialWorkspaceSurfaceForEntry(args: {
  planHandoffVisible: boolean;
  runStateKind: WorkspaceRunKind;
  /** `isActiveAutoBuild(autoBuildState)` — an in-flight auto-build is not a gate. */
  autoBuildActive: boolean;
  buildView: boolean;
  previewReady: boolean;
  planReadyMode?: Extract<WorkspaceHandoffMode, 'plan' | 'design'>;
  createHandoffMode?: Extract<WorkspaceHandoffMode, 'design'> | null;
  livePreviewEnabled: boolean;
}): InitialWorkspaceSurface {
  const showPlanModeView = planReviewNavigationEligible({
    planHandoffVisible: args.planHandoffVisible,
    runStateKind: args.runStateKind,
    autoBuildArmed: args.autoBuildActive,
  });
  return initialWorkspaceSurfaceForHandoff({
    buildView: args.buildView,
    previewReady: args.previewReady,
    showPlanModeView,
    planReadyMode: args.planReadyMode,
    createHandoffMode: args.createHandoffMode,
    livePreviewEnabled: args.livePreviewEnabled,
  });
}

/**
 * crucible#2990 — decides whether the chat surface renders in the centre
 * column (`chatModeVisible`).
 *
 * In the new UI the centre column's final ternary arm
 * (`CentreColumnWithBanner`) is reached whenever no dedicated surface
 * (Design / Preview / ProtoHub / Art) claims the current mode — including
 * when `workspaceMode` names a surface whose feature gate is OFF (e.g.
 * `'art'` without the Art flag, `'design'` without a local filesystem).
 * The old gate (`workspaceMode === 'chat'`) rendered NOTHING in those
 * fall-through states: no tabs, no composer, no error — a silent blank
 * centre column. Chat is the universal fallback surface: it renders
 * whenever the centre column is reached and plan mode isn't showing.
 */
export function centreChatSurfaceVisibleFor(args: {
  newUi: boolean;
  showPlanModeView: boolean;
  planModeVisible: boolean;
  designContentVisible: boolean;
  previewModeVisible: boolean;
  protoHubModeVisible: boolean;
  artModeVisible: boolean;
}): boolean {
  if (!args.newUi) return !args.showPlanModeView;
  if (
    args.designContentVisible ||
    args.previewModeVisible ||
    args.protoHubModeVisible ||
    args.artModeVisible
  ) {
    return false;
  }
  return !args.planModeVisible;
}

/**
 * Locks plan markdown edits after approval / during build.
 *
 * `showBackToPlan` covers quick committed/build when run-state lags.
 * `interviewStatus` covers stale `building` while the rail shows waiting.
 */
export function planRevisionLockedForHandoff(args: {
  runStateKind: WorkspaceRunKind;
  showBackToPlan: boolean;
  interviewStatus: BuildGameInterviewStatus | null | undefined;
}): boolean {
  if (args.runStateKind === 'build-kickoff-pending' || args.runStateKind === 'building') {
    return true;
  }
  if (args.showBackToPlan) return true;
  return args.interviewStatus === 'dispatched' || args.interviewStatus === 'building';
}

export function previewHandoffDisabledReasonForHandoff(args: {
  runStateKind: WorkspaceRunKind;
  previewDevLoopKind: PreviewDevLoopKind;
  previewPhaseGate: PreviewPhaseGate | undefined;
}): string | undefined {
  if (args.previewDevLoopKind !== 'idle') return undefined;
  if (args.previewPhaseGate !== undefined) return undefined;
  if (args.runStateKind === 'build-kickoff-pending' || args.runStateKind === 'building') {
    return 'Preview will unlock once the build reaches runnable code.';
  }
  return undefined;
}

export function mergePreviewDisabledReason(
  handoffReason: string | undefined,
  devLoopDisabledReason: string | undefined,
): string | undefined {
  return handoffReason ?? devLoopDisabledReason;
}

export function previewAvailableTitleForDevLoop(devLoop: PreviewDevLoopState): string {
  if (devLoop.kind === 'ready') {
    return 'Preview running — open display and controllers.';
  }
  if (devLoop.kind === 'idle') {
    return 'Start preview to launch display and controllers.';
  }
  return devLoop.detail ?? devLoop.label;
}

export type PreviewLaunchPillIcon = 'spinner' | 'lock' | 'play';

export interface PreviewLaunchPillState {
  disabled: boolean;
  phaseGated: boolean;
  title: string;
  icon: PreviewLaunchPillIcon;
}

/** Header Preview pill disabled state, title, and icon variant. */
export function derivePreviewLaunchPillState(args: {
  gameId: string | undefined;
  previewLoading: boolean;
  previewForceDisabled: boolean;
  previewDisabledReason: string | undefined;
  previewAvailableTitle: string | undefined;
  previewPhaseGate: PreviewPhaseGate | undefined;
}): PreviewLaunchPillState {
  const phaseGated = isPreviewPhaseGated(args.previewPhaseGate);
  const disabled = !args.gameId || args.previewForceDisabled || args.previewLoading || phaseGated;
  const title = !args.gameId
    ? 'Preview is unavailable until a game is loaded'
    : args.previewDisabledReason
      ? args.previewDisabledReason
      : args.previewLoading
        ? 'Starting the dev server…'
        : phaseGated && args.previewPhaseGate !== undefined
          ? previewPhaseGateTooltip(args.previewPhaseGate)
          : (args.previewAvailableTitle ?? 'Open the live preview');
  const icon: PreviewLaunchPillIcon = args.previewLoading
    ? 'spinner'
    : phaseGated || args.previewForceDisabled
      ? 'lock'
      : 'play';
  return { disabled, phaseGated, title, icon };
}

/**
 * #4053 — which of the Preview tab's three blocked sub-states currently
 * applies, or `null` when preview is not blocked. Deliberately NARROWER
 * than `derivePreviewLaunchPillState`'s `disabled` flag, which also folds
 * in `previewLoading`: `handleLaunchPreview` calls `openPreviewSurface()`
 * before starting the dev server, so a loading launch is already showing
 * the real `PreviewModule` surface (with its own loading UI) in the
 * `workspaceMode==='preview'` pane — swapping in the blocked pane during
 * that window would hide a real in-progress launch behind a static
 * message. Precedence matches `derivePreviewLaunchPillState`: no-game
 * before phase-gated before force-disabled.
 */
export function derivePreviewPaneBlockedState(args: {
  gameId: string | undefined;
  previewPhaseGate: PreviewPhaseGate | undefined;
  previewForceDisabled: boolean;
}): 'no-game' | 'phase-gated' | 'force-disabled' | null {
  if (!args.gameId) return 'no-game';
  if (isPreviewPhaseGated(args.previewPhaseGate)) return 'phase-gated';
  if (args.previewForceDisabled) return 'force-disabled';
  return null;
}

export interface PreviewBlockedPaneCopy {
  headline: string;
  body: string;
}

/**
 * Friendly full-pane copy for each of the Preview tab's blocked states
 * (#4053, resolved product decision — headline + body, NOT the tooltip
 * text reused verbatim). `previewDisabledReason` is already a specific,
 * non-generic reason for the force-disabled case (a missing capability or
 * local Node/pnpm readiness issue) — reused verbatim per that decision —
 * but callers can still land here with it `undefined` (the boolean and
 * the reason string are resolved independently in `Workspace.tsx`), so a
 * generic fallback keeps the pane from ever rendering an empty body.
 */
export function previewBlockedPaneCopy(
  state: 'no-game' | 'phase-gated' | 'force-disabled',
  args: {
    previewPhaseGate: PreviewPhaseGate | undefined;
    previewDisabledReason: string | undefined;
  },
): PreviewBlockedPaneCopy {
  if (state === 'no-game') {
    return { headline: 'Nothing to preview yet', body: 'Load or create a game first.' };
  }
  if (state === 'phase-gated') {
    const currentPhase = args.previewPhaseGate?.currentPhase;
    const phaseLabel = args.previewPhaseGate?.phaseLabel ?? 'this step';
    return {
      headline: 'Not ready to preview yet',
      body:
        currentPhase === undefined
          ? `Your build is still on ${phaseLabel}. Come back once it clears this step.`
          : `Your build is still on ${phaseLabel} (phase ${currentPhase} of ${PREVIEW_PHASE_TOTAL}). Come back once it clears this step.`,
    };
  }
  return {
    headline: "Preview isn't available right now",
    body: args.previewDisabledReason ?? 'Check back once preview requirements are ready.',
  };
}
